import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Contract, parseEther } from "ethers";
import { useWallet } from "@/context/WalletContext";
import { usePrice, usePolling, useNow } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtNum, fmtUsd, pct } from "@/lib/format";
import api from "@/lib/api";
import { CONTRACTS, ABIS, CHAIN } from "@/contracts/croc";
import { ExchangePanel } from "@/components/croc/ExchangePanel";
import { ArrowDown, Zap, Info, Wallet, Link2, CheckCircle2, Cpu, Coins, Gift, Clock, TrendingUp, Layers } from "lucide-react";

export default function BuyStake() {
  const { address, balance, user, refreshUser, getBrowserProvider, ensureNetwork, isTargetChain } = useWallet();
  const price = usePrice();
  const [searchParams] = useSearchParams();
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const [amount, setAmount] = useState("");
  const [tierKey, setTierKey] = useState(searchParams.get("tier") || "360d");
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [claiming, setClaiming] = useState(false);
  useNow(1000);

  const tiers = config?.tiers ?? [];
  const tier = tiers.find((t) => t.key === tierKey);
  const tierIndex = tiers.findIndex((t) => t.key === tierKey);
  const stakes = user?.stakes ?? [];

  // fall back to a valid tier if the ?tier= param doesn't match any known tier
  useEffect(() => {
    if (tiers.length && !tiers.find((t) => t.key === tierKey)) setTierKey("360d");
  }, [tiers]);

  const num = parseFloat(amount) || 0;
  const usd = num;
  const crocAmount = price?.price ? num / price.price : 0;
  const dailyReward = tier ? usd * tier.daily_rate : 0;

  const submitOnchain = async () => {
    if (num < 25) return toast.error("Minimum stake is $25 — enter a USDT amount of $25 or more");
    const provider = getBrowserProvider();
    if (!provider) return toast.error("Connect a real wallet (MetaMask/WalletConnect) for on-chain staking");
    if (!isTargetChain) {
      const ok = await ensureNetwork();
      if (!ok) return toast.error(`Switch your wallet to ${CHAIN.name}`);
    }
    const signer = await provider.getSigner();
    const me = await signer.getAddress();
    const usdtAbi = ["function approve(address,uint256) returns (bool)", "function allowance(address,address) view returns (uint256)"];
    const usdt = new Contract(CONTRACTS.MockUSDT, usdtAbi, signer);
    const wei = parseEther(String(num));
    const allowance = await usdt.allowance(me, CONTRACTS.CrocExchange);
    if (allowance < wei) {
      toast("Approve USDT spending…");
      await (await usdt.approve(CONTRACTS.CrocExchange, wei)).wait();
    }
    const ex = new Contract(CONTRACTS.CrocExchange, ABIS.CrocExchange, signer);
    toast("Confirm the purchase in your wallet…");
    const tx = await ex.buy(wei, tierIndex);
    toast.loading("Transaction submitted, waiting for confirmation…", { id: "tx" });
    const rcpt = await tx.wait();
    toast.dismiss("tx");
    toast.success("USDT purchase + auto-stake confirmed", { description: `Block ${rcpt.blockNumber}` });
    setReceipt({ hash: tx.hash, croc: crocAmount, bnb: num });
    try { await api.stake({ address, tier_key: tierKey, amount_usd: usd }); } catch (_) {}
    await refreshUser();
  };

  const submit = async () => {
    try {
      setLoading(true);
      await submitOnchain();
    } catch (e) {
      toast.dismiss("tx");
      const msg = e?.response?.data?.detail || e?.shortMessage || e?.message || "Transaction failed";
      toast.error(msg.slice(0, 120));
    } finally {
      setLoading(false);
    }
  };

  const claim = async () => {
    try {
      setClaiming(true);
      const res = await api.claim(address);
      toast.success("Rewards claimed", { description: `${fmtUsd(res.claimed)} added to your wallet balance.` });
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to claim");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="Staking Terminal"
        title="Buy & Auto-Stake"
        subtitle="Pick a tier, enter your USDT amount, and lock in one on-chain transaction — all five tiers and your active stakes live here."
        right={
          <button
            data-testid="claim-rewards-button"
            onClick={claim}
            disabled={claiming || (user?.pending_roi ?? 0) <= 0}
            className="flex items-center gap-2 rounded-xl bg-[#10B981] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#34D399] transition-colors disabled:opacity-50"
          >
            <Gift className="w-4 h-4" /> Claim {fmtUsd(user?.pending_roi)}
          </button>
        }
        testid="buystake-title"
      />

      <div className="croc-panel p-3 mb-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="onchain-banner">
        <Cpu className="w-4 h-4 emerald-text" /> On-chain — real USDT (BEP20) transaction on {CHAIN.name}. CrocExchange:
        <a className="emerald-text hover:underline font-num" target="_blank" rel="noreferrer" href={`${CHAIN.explorer}/address/${CONTRACTS.CrocExchange}`}>
          {CONTRACTS.CrocExchange.slice(0, 8)}…{CONTRACTS.CrocExchange.slice(-6)}
        </a>
      </div>

      {/* 1 · choose tier */}
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 mt-6">1 · Choose your staking tier</div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6" data-testid="tier-select-grid">
        {tiers.map((t, i) => {
          const selected = tierKey === t.key;
          return (
            <motion.button
              key={t.key}
              type="button"
              onClick={() => setTierKey(t.key)}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className={`text-left p-5 rounded-2xl transition-all ${
                selected ? "shimmer-border glow-emerald" : "croc-panel hover:border-[#1CFD4C]/40"
              }`}
              data-testid={`buy-tier-${t.key}`}
            >
              <div className="flex items-center justify-between">
                <Pill tone={selected ? "emerald" : "neutral"}>{t.days === 1 ? "24h" : `${t.days}d`}</Pill>
                {selected && <span className="text-[10px] uppercase tracking-widest emerald-text">Selected</span>}
              </div>
              <div className="font-display font-bold text-white text-lg mt-3">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.tagline}</div>
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="font-num font-black text-2xl gold-text">{pct(t.daily_rate * 100)}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-widest">per day</div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Lock</span><span className="font-num text-white">{t.days}d</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Max Rewards</span><span className="font-num text-white">{pct(t.max_roi * 100, 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">5% Bonus</span><span className={`font-num ${t.sponsor_bonus ? "emerald-text" : "text-muted-foreground"}`}>{t.sponsor_bonus ? "Yes" : "No"}</span></div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* 2 · amount & confirm */}
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">2 · Enter amount & confirm on-chain</div>
      <div className="grid lg:grid-cols-5 gap-6">
        <Panel className="lg:col-span-3" glow="gold" data-testid="buystake-terminal">
          <div className="rounded-2xl border border-white/10 bg-[#090E0B] p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">You pay (USDT)</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Wallet className="w-3 h-3" /> {balance ?? "0"} BNB gas
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                data-testid="buy-amount-input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent text-3xl font-num font-bold text-white w-full outline-none placeholder:text-sm placeholder:tracking-widest placeholder:uppercase placeholder:text-[rgb(28_253_76)]"
                placeholder="ENTER THE CROC"
              />
              <span className="font-num font-bold text-white shrink-0">USDT</span>
            </div>
            <div className="flex gap-2 mt-3">
              {[100, 500, 2500, 10000].map((v) => (
                <button
                  key={v}
                  data-testid={`buy-quick-${v}`}
                  onClick={() => setAmount(String(v))}
                  className="rounded-lg border border-white/10 px-3 py-1 text-xs text-muted-foreground hover:border-[#1CFD4C]/50 hover:text-white transition-colors font-num"
                >
                  {`$${v.toLocaleString()}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-center my-3">
            <div className="w-10 h-10 rounded-xl bg-[#0F1712] border border-[#1CFD4C]/30 grid place-items-center glow-gold">
              <ArrowDown className="w-4 h-4 gold-text" />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#090E0B] p-5">
            <span className="text-xs text-muted-foreground">You receive & auto-stake</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-3xl font-num font-bold emerald-text w-full">{fmtNum(crocAmount, 2)}</span>
              <span className="font-num font-bold text-white shrink-0">CROC</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2 font-num">
              1 CROC = ${fmtNum(price?.price, 5)} · pay in USDT (BEP20)
            </div>
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            <Row label="Selected tier" value={tier?.name} />
            <Row label="Lock duration" value={`${tier?.days} days`} />
            <Row label="Est. daily yield" value={pct((tier?.daily_rate ?? 0) * 100)} accent />
            <Row label="Est. daily reward" value={fmtUsd(dailyReward)} accent />
            <Row label="Direct sponsor bonus" value={tier?.sponsor_bonus ? "5% (to sponsor)" : "Excluded (24h)"} />
          </div>

          <button
            data-testid="buy-autostake-submit-button"
            onClick={submit}
            disabled={loading}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl bg-[#1CFD4C] py-3.5 font-bold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60 glow-gold"
          >
            <Zap className="w-4 h-4" /> {loading ? "Processing…" : "Approve & Stake On-chain"}
          </button>

          {receipt && (
            <div className="mt-4 rounded-xl border border-[#10B981]/25 bg-[#10B981]/[0.05] p-4" data-testid="onchain-receipt">
              <div className="flex items-center gap-2 font-display font-bold text-white mb-2">
                <CheckCircle2 className="w-4 h-4 emerald-text" /> On-chain stake confirmed
              </div>
              <div className="text-sm text-muted-foreground">Minted & staked {fmtNum(receipt.croc)} CROC for {receipt.bnb} USDT.</div>
              <a
                data-testid="tx-explorer-link"
                href={`${CHAIN.explorer}/tx/${receipt.hash}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-sm emerald-text hover:underline font-num"
              >
                <Link2 className="w-3.5 h-3.5" /> View transaction on BscScan
              </a>
            </div>
          )}
        </Panel>

        <div className="lg:col-span-2 space-y-4">
          <ExchangePanel tierIndex={tierIndex} />
          <div className="croc-panel p-4 flex items-start gap-2">
            <Info className="w-4 h-4 gold-text shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Yields are variable and revenue-backed. Each tier pays a fixed daily rate (0.1%–0.5%/day), hard-capped at 0.5%/day.
            </p>
          </div>
          <div className="croc-panel p-6 flex items-start gap-3" data-testid="usdt-note">
            <Coins className="w-5 h-5 gold-text shrink-0 mt-0.5" />
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              All purchases settle in <span className="text-white font-medium">USDT (BEP20)</span>. Selling burns your CROC and
              returns USDT at the live oracle rate after the admin deduction — the burn is provable on BscScan. Approve once,
              then buy or sell in a single transaction.
            </p>
          </div>
        </div>
      </div>

      {/* 3 · active positions */}
      <SectionTitle eyebrow="Active Positions" title="Your Stakes" testid="active-stakes-title" />
      {stakes.length === 0 ? (
        <Panel className="text-center py-14" data-testid="no-stakes">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No active stakes yet. Pick a tier above and create your first stake.</p>
        </Panel>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {stakes.map((s) => {
            const start = new Date(s.start).getTime();
            const end = new Date(s.end).getTime();
            const prog = Math.min(100, ((Date.now() - start) / (end - start)) * 100);
            return (
              <Panel key={s.id} data-testid={`active-stake-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-white">{s.tier_name}</div>
                    <div className="text-xs text-muted-foreground font-num">{fmtNum(s.croc_amount)} CROC · {fmtUsd(s.principal_usd)}</div>
                  </div>
                  <Pill tone={s.active ? "emerald" : "neutral"}>{s.active ? "Active" : "Matured"}</Pill>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <MiniStat icon={TrendingUp} label="Accrued" value={fmtUsd(s.accrued)} />
                  <MiniStat icon={Gift} label="Daily" value={pct(s.daily_rate * 100)} />
                  <MiniStat icon={Clock} label="Ends" value={new Date(s.end).toLocaleDateString()} />
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#10B981] to-[#1CFD4C]" style={{ width: `${prog}%` }} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5 font-num">{prog.toFixed(1)}% of lock elapsed</div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-num font-medium ${accent ? "emerald-text" : "text-white"}`}>{value}</span>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#090E0B] p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-num font-bold text-sm text-white mt-1">{value}</div>
    </div>
  );
}
